import {
  destroyIosBackgroundKeepalive,
  getIosBackgroundKeepaliveAudio,
  syncIosBackgroundKeepalive,
} from '@app/web-player/player/ios-background-audio';
import {PlayerStoreContext} from '@common/player/player-context';
import {IS_IOS} from '@ui/utils/platform';
import {useContext, useEffect, useRef} from 'react';

const RESUME_DELAY_MS = 250;
const RESUME_RETRY_MS = 1000;

function isBackgrounded() {
  return (
    document.visibilityState === 'hidden' ||
    (typeof document.hasFocus === 'function' && !document.hasFocus())
  );
}

/**
 * Keeps playback running across tab switches, app backgrounding, and iOS lock
 * screen until the user explicitly pauses.
 */
export function useBackgroundPlayback() {
  const store = useContext(PlayerStoreContext);
  const wasPlayingRef = useRef(false);
  const userPausedRef = useRef(false);
  const resumeTimerRef = useRef<number | null>(null);

  useEffect(() => {
    const clearResumeTimer = () => {
      if (resumeTimerRef.current != null) {
        clearTimeout(resumeTimerRef.current);
        resumeTimerRef.current = null;
      }
    };

    const resumeIfNeeded = () => {
      const {isPlaying, play} = store.getState();
      if (wasPlayingRef.current && !userPausedRef.current && !isPlaying) {
        play().catch(() => {});
      }
    };

    const scheduleResume = () => {
      clearResumeTimer();
      resumeTimerRef.current = window.setTimeout(() => {
        resumeIfNeeded();
        resumeTimerRef.current = window.setTimeout(
          resumeIfNeeded,
          RESUME_RETRY_MS,
        );
      }, RESUME_DELAY_MS);
    };

    const shouldUseKeepalive = () => {
      const {providerName, isPlaying} = store.getState();
      if (!isPlaying) return false;
      if (IS_IOS && providerName === 'htmlAudio') {
        return false;
      }
      return IS_IOS;
    };

    const syncKeepaliveAudio = (shouldPlay: boolean) => {
      if (!shouldUseKeepalive() && !shouldPlay) {
        syncIosBackgroundKeepalive(false);
        return;
      }
      if (shouldPlay && shouldUseKeepalive()) {
        syncIosBackgroundKeepalive(true);
      } else if (!shouldPlay) {
        syncIosBackgroundKeepalive(false);
      }
    };

    // Ensure keepalive element exists on iOS for the hook lifetime.
    getIosBackgroundKeepaliveAudio();

    const unsubscribe = store.subscribe((state, prev) => {
      if (state.isPlaying !== prev.isPlaying) {
        if (state.isPlaying) {
          userPausedRef.current = false;
          wasPlayingRef.current = true;
          syncKeepaliveAudio(true);
        } else if (prev.isPlaying) {
          if (isBackgrounded()) {
            // YouTube often pauses on iOS when backgrounded — treat as system pause.
            syncKeepaliveAudio(!userPausedRef.current);
            if (!userPausedRef.current) {
              scheduleResume();
            }
          } else {
            userPausedRef.current = true;
            wasPlayingRef.current = false;
            syncKeepaliveAudio(false);
          }
        }
      }

      if (prev.providerName !== state.providerName && state.isPlaying) {
        syncKeepaliveAudio(true);
      }
    });

    if ('mediaSession' in navigator) {
      const handlers: Partial<
        Record<MediaSessionAction, MediaSessionActionHandler>
      > = {
        pause: () => {
          userPausedRef.current = true;
          wasPlayingRef.current = false;
          syncKeepaliveAudio(false);
          store.getState().pause();
        },
        play: () => {
          userPausedRef.current = false;
          wasPlayingRef.current = true;
          store.getState().play().catch(() => {});
        },
        previoustrack: () => store.getState().playPrevious(),
        nexttrack: () => store.getState().playNext(),
        stop: () => {
          userPausedRef.current = true;
          wasPlayingRef.current = false;
          syncKeepaliveAudio(false);
          store.getState().stop();
        },
      };

      for (const [action, handler] of Object.entries(handlers)) {
        try {
          navigator.mediaSession.setActionHandler(
            action as MediaSessionAction,
            handler,
          );
        } catch {
          //
        }
      }
    }

    const handleBackground = () => {
      const {isPlaying} = store.getState();
      if (isPlaying) {
        wasPlayingRef.current = true;
      }
      syncKeepaliveAudio(isPlaying || wasPlayingRef.current);
      if (
        wasPlayingRef.current &&
        !userPausedRef.current &&
        'mediaSession' in navigator
      ) {
        navigator.mediaSession.playbackState = 'playing';
      }
      if (wasPlayingRef.current && !userPausedRef.current) {
        scheduleResume();
      }
    };

    const handleForeground = () => {
      resumeIfNeeded();
      syncKeepaliveAudio(store.getState().isPlaying);
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        handleBackground();
      } else {
        handleForeground();
      }
    };

    syncKeepaliveAudio(store.getState().isPlaying);
    if (store.getState().isPlaying) {
      wasPlayingRef.current = true;
    }

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('pagehide', handleBackground);
    window.addEventListener('blur', handleBackground);
    window.addEventListener('pageshow', handleForeground);
    window.addEventListener('focus', handleForeground);

    return () => {
      unsubscribe();
      clearResumeTimer();
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('pagehide', handleBackground);
      window.removeEventListener('blur', handleBackground);
      window.removeEventListener('pageshow', handleForeground);
      window.removeEventListener('focus', handleForeground);
      destroyIosBackgroundKeepalive();
    };
  }, [store]);
}
